import Box from "@mui/material/Box";
import React from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useLocation, useNavigate } from "react-router";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

interface TabbedDialogContentProps {
    customerContent?: React.ReactNode;
    headerContent?: React.ReactNode;
    vatNumContent?: React.ReactNode;
    headerChargeContent?: React.ReactNode;
    availableTabs?: string[];
}

export default function TabbedDialogContent({
    customerContent,
    headerContent,
    vatNumContent,
    headerChargeContent,
    availableTabs = ["customer", "header", "vatnum", "headercharge"],
}: TabbedDialogContentProps) {
    const location = useLocation();
    const navigate = useNavigate();

    const getTabLabels = () => ({
        customer: "Customer",
        header: "Sales Order Header",
        vatnum: "Tax Number",
        headercharge: "Header Charges",
    });

    const getTabContent = (tabKey: string) => {
        switch (tabKey) {
            case "customer":
                return customerContent;
            case "header":
                return headerContent;
            case "vatnum":
                return vatNumContent;
            case "headercharge":
                return headerChargeContent;
            default:
                return null;
        }
    };

    const getCurrentTab = () => {
        const currentPath = location.pathname;
        const pathParts = currentPath.split("/");
        const lastSegment = pathParts[pathParts.length - 1];
        const foundTab = availableTabs.find((tab) => tab === lastSegment);
        return foundTab ? availableTabs.indexOf(foundTab) : 0;
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        const selectedTab = availableTabs[newValue];
        const pathParts = location.pathname.split("/");
        // Replace the last segment with the new tab
        pathParts[pathParts.length - 1] = selectedTab;
        navigate(pathParts.join("/"));
    };

    const currentTabIndex = getCurrentTab();
    const tabLabels = getTabLabels();

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                    value={currentTabIndex}
                    onChange={handleTabChange}
                    aria-label="dialog content tabs"
                    variant="fullWidth"
                    textColor="inherit"
                >
                    {availableTabs.map((tabKey, index) => (
                        <Tab
                            key={tabKey}
                            label={tabLabels[tabKey as keyof typeof tabLabels]}
                            id={`tab-${index}`}
                            aria-controls={`tabpanel-${index}`}
                        />
                    ))}
                </Tabs>
            </Box>
            {availableTabs.map((tabKey, index) => (
                <TabPanel key={tabKey} value={currentTabIndex} index={index}>
                    {getTabContent(tabKey)}
                </TabPanel>
            ))}
        </Box>
    );
}
